/**
 * MaterialGalleryModal — two-pane material browser + editor.
 *
 * Professional-tool layout (Substance / Blender asset browser / Maya
 * Hypershade): a scrollable grid of preview spheres on the left, a
 * focused property editor on the right. Tabs switch between the current
 * scene's materials and the cross-scene library.
 *
 * The right pane embeds the shared <MaterialPropertiesEditor> so every
 * slider, color, and toggle is identical to the sidebar — no duplicated
 * controls, no visual drift.
 *
 * Material assignment is a Material Panel concern — this modal is for
 * browsing and importing materials only.
 */

import { Check, Plus, Trash2, Upload, X } from "lucide-react";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { useLibraryStore } from "../../app/libraryStore.ts";
import {
  getCachedLibraryMaterial,
  getMaterialLibraryCacheVersion,
  subscribeMaterialLibraryCache,
} from "../../app/materialLibraryCache.ts";
import { serializeMaterial } from "../../app/saveMaterialToLibrary.ts";
import { useStore } from "../../app/store.ts";
import type { SceneMaterial } from "../../modeling/types.ts";
import { Button, IconButton } from "../Button.tsx";
import { ModalShell } from "../ModalShell.tsx";
import { TabBar } from "../shared.tsx";
import { MaterialPropertiesEditor } from "../sidebar/MaterialPropertiesEditor.tsx";
import {
  COLOR_BG,
  COLOR_BORDER,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_BODY,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  RADIUS_PANEL,
  RADIUS_SM,
} from "../tokens.ts";
import {
  type GalleryItem,
  type GallerySortOrder,
  MaterialGalleryGrid,
} from "./MaterialGalleryGrid.tsx";

type GalleryTab = "scene" | "library";

interface MaterialGalleryModalProps {
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Styles — layout only. ModalShell provides the chrome, backdrop, portal,
// keyboard + outside-click handling. This style is merged into the shell so
// the gallery can use its own width/height and kill the default padding.
// ---------------------------------------------------------------------------

const SHELL_OVERRIDE_STYLE: CSSProperties = {
  width: "min(1120px, calc(100vw - 40px))",
  height: "min(720px, calc(100vh - 40px))",
  padding: 0,
  gap: 0,
  overflow: "hidden",
  borderRadius: RADIUS_PANEL,
};

const HEADER_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 20px 0 20px",
};

const TITLE_STYLE: CSSProperties = {
  fontSize: FONT_SIZE_MODAL_TITLE,
  fontWeight: 600,
  color: COLOR_TEXT,
};

const BODY_STYLE: CSSProperties = {
  flex: 1,
  display: "flex",
  minHeight: 0,
};

const DETAILS_PANE_STYLE: CSSProperties = {
  width: 360,
  flexShrink: 0,
  borderLeft: `0.5px solid ${COLOR_BORDER}`,
  display: "flex",
  flexDirection: "column",
  background: `${COLOR_BG}40`,
};

const DETAILS_HEADER_STYLE: CSSProperties = {
  padding: "14px 16px 10px 16px",
  borderBottom: `0.5px solid ${COLOR_BORDER}`,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const DETAILS_BODY_STYLE: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "10px 14px 16px 14px",
};

const FOOTER_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
  padding: "12px 20px",
  borderTop: `0.5px solid ${COLOR_BORDER}`,
};

const NAME_INPUT_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "6px 10px",
  borderRadius: RADIUS_SM,
  border: `1px solid ${COLOR_BORDER}`,
  background: COLOR_SURFACE,
  color: COLOR_TEXT,
  fontSize: FONT_SIZE_BODY,
  outline: "none",
};

// ---------------------------------------------------------------------------
// Derived item lists
// ---------------------------------------------------------------------------

function useSceneItems(): GalleryItem[] {
  const materials = useStore((s) => s.materials);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const libraryItems = useLibraryStore((s) => s.items);
  useMaterialCacheVersion();
  return useMemo(() => {
    // Pre-compute the set of serialized library material strings so each
    // scene material can check "am I already in the library?" in O(1).
    const librarySerials = new Set<string>();
    for (const item of libraryItems) {
      if (item.type !== "material") continue;
      const cached = getCachedLibraryMaterial(item.id);
      if (!cached) continue;
      librarySerials.add(serializeMaterial(cached));
    }

    const userCounts = new Map<string, number>();
    for (const o of sceneObjects.values()) {
      for (const slot of o.materialSlots) {
        userCounts.set(
          slot.materialId,
          (userCounts.get(slot.materialId) ?? 0) + 1,
        );
      }
    }
    const items: GalleryItem[] = [];
    for (const block of materials.values()) {
      items.push({
        id: block.id,
        name: block.name,
        properties: block.properties,
        linked: librarySerials.has(serializeMaterial(block.properties)),
        userCount: userCounts.get(block.id) ?? 0,
        // Scene blocks have no timestamp; insertion order via 0 makes the
        // "Recent" sort degrade gracefully to Map order.
        updatedAt: 0,
      });
    }
    return items;
    // cacheVersion participates so the memo recomputes when library cache updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materials, sceneObjects, libraryItems]);
}

/**
 * Bridge the module-level material cache into React. Re-renders when
 * any cache entry changes (new JSON fetched, edit applied).
 */
function useMaterialCacheVersion(): number {
  return useSyncExternalStore(
    subscribeMaterialLibraryCache,
    getMaterialLibraryCacheVersion,
    getMaterialLibraryCacheVersion,
  );
}

function useLibraryItems(): GalleryItem[] {
  const libraryItems = useLibraryStore((s) => s.items);
  useMaterialCacheVersion();
  return useMemo(() => {
    const items: GalleryItem[] = [];
    for (const item of libraryItems) {
      if (item.type !== "material") continue;
      const cached = getCachedLibraryMaterial(item.id);
      if (!cached) continue; // skip while the JSON is still in flight
      items.push({
        id: item.id,
        name: item.name,
        properties: cached,
        linked: false,
        userCount: 0,
        updatedAt: new Date(item.updatedAt).getTime(),
      });
    }
    return items;
    // version participates so the memo recomputes when cache entries arrive
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItems]);
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

/**
 * Inline rename input with an internal draft. Resets the draft whenever the
 * `id` prop changes (a different material was selected), but does NOT reset
 * on external name updates — so an in-flight rename isn't clobbered by a
 * linked-material propagation tick.
 */
function NameEditor({
  name,
  onCommit,
}: {
  id: string;
  name: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(name);
  // Reset only on identity change — preserves the user's in-flight typing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDraft(name), [name]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== name) onCommit(trimmed);
    else setDraft(name);
  };

  return (
    <input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") {
          setDraft(name);
          (e.target as HTMLInputElement).blur();
        }
      }}
      style={NAME_INPUT_STYLE}
    />
  );
}

/**
 * Generic details-pane shell used by both tabs. Renders the name editor,
 * a metadata slot, the action button row, and — when properties are provided
 * — the embedded property editor. Library materials omit the editor since the
 * library is an immutable, copy-from source; a prompt to import is shown instead.
 */
function DetailsPane({
  id,
  name,
  onRename,
  metadata,
  actions,
  properties,
  onPropertyChange,
}: {
  id: string;
  name: string;
  onRename: (next: string) => void;
  metadata: ReactNode;
  actions: ReactNode;
  properties?: SceneMaterial;
  onPropertyChange?: (patch: Partial<SceneMaterial>) => void;
}) {
  return (
    <>
      <div style={DETAILS_HEADER_STYLE}>
        <NameEditor id={id} name={name} onCommit={onRename} />
        {metadata}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {actions}
        </div>
      </div>
      <div style={DETAILS_BODY_STYLE}>
        {properties && onPropertyChange ? (
          <MaterialPropertiesEditor
            properties={properties}
            onChange={onPropertyChange}
          />
        ) : (
          <div
            style={{
              padding: "20px 4px",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_SMALL,
              textAlign: "center",
            }}
          >
            Import to your scene to edit this material's properties.
          </div>
        )}
      </div>
    </>
  );
}

function MetadataRow({
  userCount,
  inLibrary,
  hint,
}: {
  userCount?: number;
  /** True when this material's properties exactly match a library entry. */
  inLibrary?: boolean;
  hint?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: FONT_SIZE_TINY,
        color: COLOR_TEXT_FAINT,
      }}
    >
      {userCount != null && (
        <span>
          {userCount} {userCount === 1 ? "user" : "users"}
        </span>
      )}
      {inLibrary && (
        <>
          {userCount != null && <span>·</span>}
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              color: COLOR_TEXT_DIM,
            }}
          >
            <Check size={10} /> In Library
          </span>
        </>
      )}
      {hint && (
        <span
          style={{ marginLeft: inLibrary || userCount != null ? "auto" : 0 }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

function EmptyDetails({ message }: { message: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        color: COLOR_TEXT_FAINT,
        fontSize: FONT_SIZE_SMALL,
        textAlign: "center",
      }}
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scene-tab details
// ---------------------------------------------------------------------------

function SceneDetails({ materialId }: { materialId: string }) {
  const block = useStore((s) => s.materials.get(materialId));
  const userCount = useStore((s) => {
    let n = 0;
    for (const o of s.sceneObjects.values()) {
      for (const slot of o.materialSlots)
        if (slot.materialId === materialId) n++;
    }
    return n;
  });
  const updateMaterial = useStore((s) => s.updateMaterial);
  const renameMaterial = useStore((s) => s.renameMaterial);
  const deleteMaterial = useStore((s) => s.deleteMaterial);
  const exportToLibrary = useStore((s) => s.exportMaterialToLibrary);
  const libraryItems = useLibraryStore((s) => s.items);
  useMaterialCacheVersion();

  const inLibrary = useMemo(() => {
    if (!block) return false;
    const blockSerial = serializeMaterial(block.properties);
    for (const item of libraryItems) {
      if (item.type !== "material") continue;
      const cached = getCachedLibraryMaterial(item.id);
      if (!cached) continue;
      if (serializeMaterial(cached) === blockSerial) return true;
    }
    return false;
    // cacheVersion ensures this recomputes when cache updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block?.properties, libraryItems, block]);

  if (!block) {
    return <EmptyDetails message="This material no longer exists." />;
  }

  const actions = (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          void exportToLibrary(block.id);
        }}
        title="Copy this material to your library"
        leftIcon={<Upload size={12} />}
      >
        Save to Library
      </Button>
      <IconButton
        onClick={() => deleteMaterial(block.id)}
        title="Delete material"
        label="Delete material"
        icon={<Trash2 size={12} />}
        variant="secondary"
        size={30}
        style={{ marginLeft: "auto" }}
      />
    </>
  );

  return (
    <DetailsPane
      id={block.id}
      name={block.name}
      onRename={(next) => renameMaterial(block.id, next)}
      metadata={<MetadataRow userCount={userCount} inLibrary={inLibrary} />}
      actions={actions}
      properties={block.properties}
      onPropertyChange={(patch) => updateMaterial(block.id, patch)}
    />
  );
}

// ---------------------------------------------------------------------------
// Library-tab details
// ---------------------------------------------------------------------------

function LibraryDetails({
  libraryId,
  onImported,
}: {
  libraryId: string;
  /** Called with the new scene material id after a successful import. */
  onImported: (newSceneMaterialId: string) => void;
}) {
  const item = useLibraryStore(
    (s) => s.items.find((i) => i.id === libraryId) ?? null,
  );
  const renameItem = useLibraryStore((s) => s.renameItem);
  const deleteItems = useLibraryStore((s) => s.deleteItems);
  const importFromLibrary = useStore((s) => s.importMaterialFromLibrary);
  useMaterialCacheVersion();
  const allSceneMaterials = useStore((s) => s.materials);

  // Count scene materials whose properties exactly match this library entry.
  const sceneCopyCount = useMemo(() => {
    const cached = getCachedLibraryMaterial(libraryId);
    if (!cached) return 0;
    const libSerial = serializeMaterial(cached);
    let n = 0;
    for (const b of allSceneMaterials.values()) {
      if (serializeMaterial(b.properties) === libSerial) n++;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId, allSceneMaterials]);

  if (!item || item.type !== "material") {
    return <EmptyDetails message="This library material no longer exists." />;
  }

  const handleImport = () => {
    const newId = importFromLibrary(item.id);
    if (!newId) return;
    onImported(newId);
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={handleImport}
        leftIcon={<Plus size={12} />}
      >
        Import to Scene
      </Button>
      <IconButton
        onClick={() => {
          void deleteItems([item.id]);
        }}
        title="Delete library material"
        label="Delete library material"
        icon={<Trash2 size={12} />}
        variant="secondary"
        size={30}
        style={{ marginLeft: "auto" }}
      />
    </>
  );

  return (
    <DetailsPane
      id={item.id}
      name={item.name}
      onRename={(next) => {
        void renameItem(item.id, next);
      }}
      metadata={
        <MetadataRow
          userCount={sceneCopyCount}
          hint={sceneCopyCount === 0 ? "Not yet imported" : undefined}
        />
      }
      actions={actions}
    />
  );
}

// ---------------------------------------------------------------------------
// Top-level modal
// ---------------------------------------------------------------------------

const TABS = [
  { id: "scene" as const, label: "Scene Materials" },
  { id: "library" as const, label: "Library" },
];

export function MaterialGalleryModal({
  open,
  onClose,
}: MaterialGalleryModalProps) {
  const [tab, setTab] = useState<GalleryTab>("scene");
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [selectedLibraryId, setSelectedLibraryId] = useState<string | null>(
    null,
  );
  const [sortOrder, setSortOrder] = useState<GallerySortOrder>("recent");

  const sceneItems = useSceneItems();
  const libraryItems = useLibraryItems();

  // Seed selection when the modal opens. Intentionally only keys on `open`
  // so the user's selection is not clobbered by later item-list updates.
  useEffect(() => {
    if (!open) return;
    if (!selectedSceneId) {
      setSelectedSceneId(sceneItems[0]?.id ?? null);
    }
    if (!selectedLibraryId && libraryItems.length > 0) {
      setSelectedLibraryId(libraryItems[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    libraryItems.length,
    libraryItems[0]?.id,
    sceneItems[0]?.id,
    selectedLibraryId,
    selectedSceneId,
  ]);

  // Reset local selection on close so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setSelectedSceneId(null);
      setSelectedLibraryId(null);
      setTab("scene");
    }
  }, [open]);

  const activeItems = tab === "scene" ? sceneItems : libraryItems;
  const activeSelectedId =
    tab === "scene" ? selectedSceneId : selectedLibraryId;
  const setActiveSelectedId =
    tab === "scene" ? setSelectedSceneId : setSelectedLibraryId;

  const emptyCopy =
    tab === "scene"
      ? {
          title: "No materials in this scene",
          subtitle:
            "Create a material from the sidebar or import one from the library.",
        }
      : {
          title: "Your material library is empty",
          subtitle:
            "Save a scene material to the library to make it available everywhere.",
        };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      portal
      style={SHELL_OVERRIDE_STYLE}
    >
      <div style={HEADER_STYLE}>
        <div style={TITLE_STYLE}>Material Gallery</div>
        <IconButton
          onClick={onClose}
          title="Close"
          label="Close"
          icon={<X size={16} />}
          size={28}
          style={{ color: COLOR_TEXT_DIM }}
        />
      </div>

      <div style={{ padding: "8px 20px 0 20px" }}>
        <TabBar
          tabs={TABS}
          active={tab}
          onChange={setTab}
          variant="underline"
        />
      </div>

      <div style={BODY_STYLE}>
        <MaterialGalleryGrid
          items={activeItems}
          selectedId={activeSelectedId}
          sortOrder={sortOrder}
          onSortChange={setSortOrder}
          onSelect={setActiveSelectedId}
          emptyTitle={emptyCopy.title}
          emptySubtitle={emptyCopy.subtitle}
        />
        <div style={DETAILS_PANE_STYLE}>
          {tab === "scene" &&
            (selectedSceneId ? (
              <SceneDetails materialId={selectedSceneId} />
            ) : (
              <EmptyDetails message="Select a material to edit its properties." />
            ))}
          {tab === "library" &&
            (selectedLibraryId ? (
              <LibraryDetails
                libraryId={selectedLibraryId}
                onImported={(id) => {
                  setTab("scene");
                  setSelectedSceneId(id);
                }}
              />
            ) : (
              <EmptyDetails message="Select a library material to view or import." />
            ))}
        </div>
      </div>

      <div style={FOOTER_STYLE}>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </div>
    </ModalShell>
  );
}
